import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { publicClient, customerClient } from "../api";
import { useI18n } from "../i18n";
import { Plus, Minus, ShoppingBag, MapPin, Phone, Clock, ChevronLeft, LogOut } from "lucide-react";

const COVER_FALLBACK = "https://images.pexels.com/photos/35065203/pexels-photo-35065203.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const DISH_FALLBACK = "https://images.unsplash.com/photo-1669109230787-71bdd4699af4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHw0fHxkZWxpY2lvdXMlMjBmb29kJTIwcGxhdGUlMjByZXN0YXVyYW50fGVufDB8fHx8MTc3ODU4MjUzMHww&ixlib=rb-4.1.0&q=85";

const LANGS = [{ code: "it", label: "IT" }, { code: "en", label: "EN" }, { code: "es", label: "ES" }];

function getCustomer() {
  try { return JSON.parse(localStorage.getItem("foodgo_customer") || "null"); } catch { return null; }
}

export default function RestaurantPublic() {
  const { slug } = useParams();
  const { t, lang, setLang } = useI18n();
  const [data, setData] = useState(null);
  const [cart, setCart] = useState({});
  const [urgency, setUrgency] = useState(false);
  const [tip, setTip] = useState(0);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [customer, setCustomer] = useState({ name: "", phone: "", address: "", notes: "" });
  const [orderType, setOrderType] = useState("delivery");
  const [submitting, setSubmitting] = useState(false);
  const [authedCustomer, setAuthedCustomer] = useState(getCustomer());
  const [loyalty, setLoyalty] = useState(null);
  const [pointsToRedeem, setPointsToRedeem] = useState(0);

  useEffect(() => {
    // Restore cart from session if any
    try {
      const saved = sessionStorage.getItem(`foodgo_cart_${slug}`);
      if (saved) setCart(JSON.parse(saved));
    } catch { /* ignore */ }
    (async () => {
      try {
        const { data } = await publicClient.get(`/public/restaurant/${slug}`);
        setData(data);
      } catch (err) {
        if (err?.response?.status === 423) {
          setData({ error: true, locked: true });
        } else {
          setData({ error: true });
        }
      }
    })();
  }, [slug]);

  useEffect(() => {
    try { sessionStorage.setItem(`foodgo_cart_${slug}`, JSON.stringify(cart)); } catch { /* ignore */ }
  }, [cart, slug]);

  // Auto-fill from logged customer
  useEffect(() => {
    if (authedCustomer && !customer.name) {
      setCustomer((c) => ({ ...c, name: authedCustomer.name || "" }));
    }
  }, [authedCustomer]); // eslint-disable-line react-hooks/exhaustive-deps

  // Loyalty: load points + config for this restaurant when customer logs in
  useEffect(() => {
    if (!authedCustomer) { setLoyalty(null); return; }
    (async () => {
      try {
        const { data: l } = await customerClient.get(`/loyalty/restaurant/${slug}/me`);
        setLoyalty(l);
      } catch { setLoyalty(null); }
    })();
  }, [authedCustomer, slug]);

  // Active orders at this restaurant (up to 3) for live status widget
  const [activeOrders, setActiveOrders] = useState([]);
  useEffect(() => {
    try { localStorage.setItem("foodgo_last_slug", slug); } catch { /* ignore */ }
  }, [slug]);
  useEffect(() => {
    if (!authedCustomer) { setActiveOrders([]); return; }
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await customerClient.get(`/orders/customer/active-at/${slug}`);
        if (alive) setActiveOrders(Array.isArray(data) ? data : (data ? [data] : []));
      } catch (e) { /* ignore */ }
    };
    tick();
    const id = setInterval(tick, 10000);
    return () => { alive = false; clearInterval(id); };
  }, [authedCustomer, slug]);

  if (!data) return <div className="min-h-screen flex items-center justify-center bg-deep"><div className="w-10 h-10 rounded-full border-4 border-line border-t-[#00E5FF] animate-spin" /></div>;
  if (data.error) {
    if (data.locked) {
      return (
        <div className="min-h-screen flex items-center justify-center p-6 bg-deep">
          <div className="max-w-md text-center" data-testid="storefront-locked">
            <div className="w-16 h-16 rounded-full bg-red-500/20 border border-red-500/40 flex items-center justify-center mx-auto">
              <span className="text-2xl">⏸</span>
            </div>
            <h1 className="font-heading text-3xl mt-5">{t.storefront.lockedTitle}</h1>
            <p className="text-zinc-400 mt-2 text-sm">{t.storefront.lockedSub}</p>
          </div>
        </div>
      );
    }
    return <div className="min-h-screen flex items-center justify-center p-6 text-center text-zinc-400 bg-deep">Restaurant not found.</div>;
  }

  const { restaurant: r, dishes } = data;
  const primary = r.primary_color || "#7C3AED";
  const categories = Array.from(new Set(dishes.map((d) => d.category || "Menu")));

  const inc = (id) => setCart((c) => ({ ...c, [id]: (c[id] || 0) + 1 }));
  const dec = (id) => setCart((c) => {
    const nv = Math.max(0, (c[id] || 0) - 1);
    const next = { ...c };
    if (nv === 0) delete next[id]; else next[id] = nv;
    return next;
  });
  const cartItems = Object.entries(cart).map(([id, qty]) => {
    const d = dishes.find((x) => x.id === id);
    return d ? { id, qty, price: d.price, name: d.name } : null;
  }).filter(Boolean);

  const subtotal = cartItems.reduce((s, i) => s + i.price * i.qty, 0);
  const discount = subtotal * ((r.promo_discount_percent || 0) / 100);
  const urgencyFee = urgency && r.urgency_enabled ? (r.urgency_fee || 0) : 0;
  const deliveryFee = orderType === "delivery" ? (r.delivery_fee || 0) : 0;
  const tipNum = parseFloat(tip || 0) || 0;
  const loyaltyEur = (loyalty?.enabled && pointsToRedeem > 0) ? Math.min(pointsToRedeem * (loyalty.euro_per_point || 0), Math.max(0, subtotal - discount)) : 0;
  const total = Math.max(0, subtotal - discount - loyaltyEur + urgencyFee + deliveryFee + tipNum);

  const startGoogleLogin = () => {
    sessionStorage.setItem("foodgo_customer_return_slug", slug);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard"; // any internal route — AuthCallback intercepts the hash
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logoutCustomer = () => {
    localStorage.removeItem("foodgo_customer_token");
    localStorage.removeItem("foodgo_customer");
    setAuthedCustomer(null);
  };

  const submitOrder = async (e, payMethod) => {
    e.preventDefault();
    if (cartItems.length === 0) return;
    if (!authedCustomer) { startGoogleLogin(); return; }
    setSubmitting(true);
    try {
      const origin_url = window.location.origin;
      const items = cartItems.map((i) => ({ dish_id: i.id, qty: i.qty }));
      const { data } = await customerClient.post("/orders/checkout", {
        restaurant_slug: slug,
        customer_name: customer.name,
        customer_phone: customer.phone,
        address: orderType === "delivery" ? customer.address : "",
        notes: customer.notes,
        items, tip: tipNum, urgency, order_type: orderType,
        payment_method: payMethod || "stripe",
        points_redeemed: (loyalty?.enabled && pointsToRedeem > 0) ? Math.floor(pointsToRedeem) : 0,
        origin_url,
      });
      if ((data.cash || data.paypal || data.satispay) && data.redirect) {
        try { sessionStorage.removeItem(`foodgo_cart_${slug}`); } catch { /* ignore */ }
        window.location.href = data.redirect;
      } else if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      console.error(err);
      alert("Errore: " + (err?.response?.data?.detail || err.message));
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-deep">
      {/* Cover */}
      <div className="relative">
        <img src={r.cover_url || COVER_FALLBACK} alt={r.name} className="w-full h-72 sm:h-96 object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#06061a]/95 via-[#06061a]/40 to-transparent" />
        <div className="absolute top-4 left-4 flex gap-2">
          {authedCustomer ? (
            <Link to="/my-orders" className="bg-deep-card border border-line rounded-full px-3 py-2 hover:border-[#00E5FF] transition inline-flex items-center gap-1.5 text-xs text-zinc-200 font-mono uppercase tracking-widest" data-testid="public-my-orders-link">
              <ShoppingBag className="w-3.5 h-3.5" /> {t.storefront.myOrdersLink || "I miei ordini"}
            </Link>
          ) : (
            <span className="bg-deep-card border border-line rounded-full p-2 inline-flex items-center" data-testid="public-back">
              <ChevronLeft className="w-4 h-4 text-zinc-500" />
            </span>
          )}
        </div>
        <div className="absolute top-4 right-4 flex items-center gap-2">
          {authedCustomer ? (
            <>
              {loyalty?.enabled && (
                <span className="chip chip-yellow !text-[10px] font-semibold" data-testid="header-points-chip">
                  ⭐ {t.storefront.pointsLabel || "Punti"}: {loyalty.balance || 0}
                </span>
              )}
              <div className="chip chip-cyan !text-[10px]" data-testid="customer-chip">
                {t.storefront.loggedAs}: {authedCustomer.name?.split(" ")[0] || authedCustomer.email}
                <button onClick={logoutCustomer} className="ml-2 text-zinc-300 hover:text-red-400" data-testid="customer-logout">
                  <LogOut className="w-3 h-3" />
                </button>
              </div>
            </>
          ) : (
            <button onClick={startGoogleLogin} className="btn-primary !py-1.5 !px-3 text-xs inline-flex items-center gap-1.5" data-testid="google-signup-btn">
              <svg className="w-3.5 h-3.5" viewBox="0 0 48 48" aria-hidden>
                <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.4 6.3 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.4-.4-3.5z"/>
                <path fill="#FF3D00" d="M6.3 14.1l6.6 4.8C14.6 15.1 18.9 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.4 6.3 29.5 4 24 4 16.1 4 9.3 8.5 6.3 14.1z"/>
                <path fill="#4CAF50" d="M24 44c5.3 0 10.1-2 13.7-5.3l-6.3-5.3c-2 1.4-4.5 2.3-7.4 2.3-5.3 0-9.7-3.3-11.3-8l-6.5 5C9 39.4 15.9 44 24 44z"/>
                <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.4l6.3 5.3C40.6 35.5 44 30.2 44 24c0-1.2-.1-2.4-.4-3.5z"/>
              </svg>
              {t.storefront.googleSignup || "Iscriviti con Google"}
            </button>
          )}
          <select value={lang} onChange={(e) => setLang(e.target.value)} className="bg-deep-card border border-line text-zinc-200 rounded-full px-3 py-1.5 text-xs font-mono uppercase" data-testid="public-lang">
            {LANGS.map((l) => (<option key={l.code} value={l.code}>{l.label}</option>))}
          </select>
        </div>
        <div className="absolute bottom-0 left-0 right-0">
          <div className="max-w-7xl mx-auto px-6 pb-6 flex items-end gap-4">
            {r.logo_url && (<img src={r.logo_url} alt="logo" className="w-20 h-20 rounded-2xl border-2 border-white/30 object-cover bg-white/5 backdrop-blur" />)}
            <div className="text-white">
              <h1 className="font-heading font-black text-3xl sm:text-5xl tracking-tighter leading-none">{r.name}</h1>
              <div className="mt-2 flex flex-wrap gap-2 text-xs">
                {(r.is_open_now === false || r.is_open === false) ? (
                  <span className="inline-flex items-center gap-1 bg-red-500/20 border border-red-500/50 text-red-200 px-3 py-1 rounded-full font-mono uppercase tracking-widest" data-testid="closed-badge">
                    <span className="inline-block w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                    {r.is_open === false ? (t.storefront.closedBadge || "Chiuso") : (t.storefront.outsideHours || "Fuori orario")}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 bg-neon-cyan/15 border border-neon-cyan/50 text-neon-cyan px-3 py-1 rounded-full font-mono uppercase tracking-widest" data-testid="open-badge">
                    <span className="inline-block w-2 h-2 rounded-full bg-neon-cyan animate-pulse" /> {t.storefront.openBadge || "Aperto"}
                  </span>
                )}
                <span className="inline-flex items-center gap-1 bg-deep-card/80 backdrop-blur border border-line text-zinc-200 px-3 py-1 rounded-full"><Clock className="w-3 h-3" /> {t.storefront.deliveryTime.replace("{min}", r.delivery_time_min || 30)}</span>
                {r.address && <span className="inline-flex items-center gap-1 bg-deep-card/80 backdrop-blur border border-line text-zinc-200 px-3 py-1 rounded-full"><MapPin className="w-3 h-3" /> {r.address}</span>}
                {r.phone && <span className="inline-flex items-center gap-1 bg-deep-card/80 backdrop-blur border border-line text-zinc-200 px-3 py-1 rounded-full"><Phone className="w-3 h-3" /> {r.phone}</span>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {r.promo_discount_percent > 0 && (
        <div className="bg-neon-yellow text-[#06061a]">
          <div className="max-w-7xl mx-auto px-6 py-2.5 text-sm font-semibold flex items-center gap-2" data-testid="promo-banner">
            <span className="inline-block w-2 h-2 rounded-full bg-[#06061a] animate-pulse" />
            {t.storefront.promoBanner.replace("{p}", r.promo_discount_percent)}
            {r.promo_message && <span className="font-normal">— {r.promo_message}</span>}
          </div>
        </div>
      )}

      {activeOrders.length > 0 && (
        <div className="max-w-7xl mx-auto px-6 pt-6 space-y-3" data-testid="active-orders-stack">
          {activeOrders.length > 1 && (
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-[0.22em] text-neon-cyan font-mono">
                {(t.storefront.activeOrdersCount || "{n} ordini in corso").replace("{n}", activeOrders.length)}
              </span>
              <span className="text-[10px] font-mono text-zinc-500">· max 3</span>
            </div>
          )}
          {activeOrders.map((o) => <ActiveOrderBanner key={o.id} order={o} t={t} />)}
        </div>
      )}

      <div className="max-w-7xl mx-auto px-6 py-10 grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8">
          {r.description && <p className="text-zinc-400 mb-8 max-w-2xl">{r.description}</p>}
          {categories.map((cat) => (
            <section key={cat} className="mb-12">
              <h2 className="font-heading text-2xl tracking-tight mb-5">{cat}</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {dishes.filter((d) => (d.category || "Menu") === cat).map((d) => (
                  <article key={d.id} className="card-neon overflow-hidden flex" data-testid={`dish-${d.id}`}>
                    <div className="flex-1 p-5">
                      <h3 className="font-heading font-semibold text-lg">{d.name}</h3>
                      <p className="text-sm text-zinc-400 mt-1 line-clamp-2">{d.description}</p>
                      {Array.isArray(d.allergens) && d.allergens.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1" data-testid={`dish-allergens-${d.id}`}>
                          {d.allergens.slice(0, 6).map((a) => (
                            <span key={a} className="chip !text-[9px] !py-0.5 !px-1.5 border-amber-500/40 text-amber-300">{a}</span>
                          ))}
                        </div>
                      )}
                      <div className="mt-3 flex items-center justify-between">
                        <div className="font-heading font-bold text-neon-cyan">€ {Number(d.price).toFixed(2)}</div>
                        {cart[d.id] ? (
                          <div className="flex items-center gap-1.5">
                            <button onClick={() => dec(d.id)} className="w-8 h-8 rounded-full border border-line bg-deep-card flex items-center justify-center text-zinc-200" data-testid={`dec-${d.id}`}><Minus className="w-3 h-3" /></button>
                            <div className="w-7 text-center font-semibold">{cart[d.id]}</div>
                            <button onClick={() => inc(d.id)} className="w-8 h-8 rounded-full text-[#06061a] flex items-center justify-center font-bold" style={{ background: primary, boxShadow: `0 0 18px ${primary}88` }} data-testid={`inc-${d.id}`}><Plus className="w-3 h-3" /></button>
                          </div>
                        ) : (
                          <button onClick={() => inc(d.id)} className="text-[#06061a] text-xs font-bold px-4 py-2 rounded-full font-mono uppercase tracking-wider" style={{ background: primary, boxShadow: `0 0 22px ${primary}99` }} data-testid={`add-${d.id}`}>
                            <Plus className="w-3 h-3 inline mr-1" /> {t.storefront.add}
                          </button>
                        )}
                      </div>
                    </div>
                    <img src={d.image_url || DISH_FALLBACK} alt={d.name} className="w-32 h-32 object-cover" />
                  </article>
                ))}
              </div>
            </section>
          ))}
        </div>

        <aside className="lg:col-span-4">
          <div className="sticky top-6 card-neon p-6" data-testid="cart-box">
            <div className="flex items-center gap-2 mb-4">
              <ShoppingBag className="w-5 h-5" style={{ color: primary }} />
              <h3 className="font-heading font-semibold text-lg">{t.storefront.cart}</h3>
            </div>
            {cartItems.length === 0 ? (
              <p className="text-zinc-500 text-sm">{t.storefront.empty}</p>
            ) : (
              <>
                <ul className="space-y-2 mb-4 max-h-80 overflow-auto scrollbar-thin">
                  {cartItems.map((i) => (
                    <li key={i.id} className="flex items-center justify-between text-sm text-zinc-200">
                      <span>{i.qty}× {i.name}</span>
                      <span className="font-semibold">€ {(i.price * i.qty).toFixed(2)}</span>
                    </li>
                  ))}
                </ul>
                {r.tip_enabled && (
                  <div className="mb-3">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-neon-cyan font-mono mb-2">{t.storefront.tip}</div>
                    <div className="flex gap-2">
                      {[0, 1, 2, 3].map((v) => (
                        <button key={v} onClick={() => setTip(v)}
                          className={`flex-1 py-2 rounded-full text-xs font-semibold border transition ${parseFloat(tip) === v ? "bg-neon-cyan text-[#06061a] border-[#00E5FF]" : "bg-deep-card border-line text-zinc-300"}`}
                          data-testid={`tip-${v}`}>{v === 0 ? "—" : `€${v}`}</button>
                      ))}
                    </div>
                  </div>
                )}
                {r.urgency_enabled && (
                  <label className="flex items-center justify-between gap-3 py-3 border-y border-line my-3" data-testid="urgency-row">
                    <div>
                      <div className="text-sm font-semibold text-zinc-200">{r.urgency_label || t.storefront.urgency}</div>
                      <div className="text-xs text-neon-yellow font-mono">+ € {Number(r.urgency_fee || 0).toFixed(2)}</div>
                    </div>
                    <input type="checkbox" checked={urgency} onChange={(e) => setUrgency(e.target.checked)} className="w-5 h-5 accent-[#00E5FF]" data-testid="urgency-checkbox" />
                  </label>
                )}
                <div className="space-y-1 text-sm">
                  <Row label={t.storefront.subtotal} value={`€ ${subtotal.toFixed(2)}`} />
                  {discount > 0 && <Row label={t.storefront.discount} value={`- € ${discount.toFixed(2)}`} className="text-neon-yellow" />}
                  {loyaltyEur > 0 && <Row label={(t.storefront.loyaltyDiscount || "Sconto punti")} value={`- € ${loyaltyEur.toFixed(2)}`} className="text-neon-cyan" />}
                  {deliveryFee > 0 && <Row label={t.storefront.delivery} value={`€ ${deliveryFee.toFixed(2)}`} />}
                  {urgencyFee > 0 && <Row label={t.storefront.urgency} value={`€ ${urgencyFee.toFixed(2)}`} />}
                  {tipNum > 0 && <Row label={t.storefront.tip} value={`€ ${tipNum.toFixed(2)}`} />}
                  <div className="border-t border-line pt-2 mt-2 flex justify-between font-heading font-bold text-lg">
                    <span>{t.storefront.total}</span>
                    <span className="text-neon-cyan text-glow-cyan">€ {total.toFixed(2)}</span>
                  </div>
                </div>

                {loyalty?.enabled && authedCustomer && loyalty.balance > 0 && (() => {
                  const effectiveMin = Math.max(20, loyalty.min_points_to_redeem || 0);
                  const canRedeem = loyalty.balance >= effectiveMin;
                  const maxRedeem = Math.min(loyalty.balance, Math.floor((subtotal - discount) / (loyalty.euro_per_point || 1)));
                  return (
                  <div className="mt-4 p-3 rounded-xl border border-neon-cyan/40 bg-neon-cyan/5" data-testid="loyalty-box">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="text-xs font-mono uppercase tracking-widest text-neon-cyan">
                        {(t.storefront.loyaltyTitle || "I tuoi punti")}: <span className="font-bold" data-testid="loyalty-balance">{loyalty.balance}</span>
                      </div>
                      <div className="text-[10px] text-zinc-400 font-mono">
                        1 pt = € {Number(loyalty.euro_per_point || 0).toFixed(2)}
                      </div>
                    </div>
                    {canRedeem ? (
                      <>
                        <div className="flex items-center gap-2 mt-2">
                          <input
                            type="range"
                            min={0}
                            max={maxRedeem}
                            step={1}
                            value={pointsToRedeem}
                            onChange={(e) => setPointsToRedeem(parseInt(e.target.value || 0))}
                            className="flex-1 accent-[#00E5FF]"
                            data-testid="loyalty-slider"
                          />
                          <input
                            type="number"
                            min={0}
                            max={loyalty.balance}
                            value={pointsToRedeem}
                            onChange={(e) => setPointsToRedeem(Math.max(0, Math.min(loyalty.balance, parseInt(e.target.value || 0))))}
                            className="w-20 input-neon !py-1 !text-xs"
                            data-testid="loyalty-input"
                          />
                        </div>
                        {pointsToRedeem > 0 && pointsToRedeem < effectiveMin && (
                          <p className="text-[11px] text-red-300 mt-1 font-mono" data-testid="loyalty-min-warning">
                            {(t.storefront.loyaltyMinHint || "Minimo {n} punti per riscattare").replace("{n}", effectiveMin)}
                          </p>
                        )}
                      </>
                    ) : (
                      <p className="text-[11px] text-zinc-400 mt-2 font-mono" data-testid="loyalty-need-more">
                        {(t.storefront.loyaltyNeedMore || "Servono almeno {n} punti per riscattare. Te ne mancano {x}.")
                          .replace("{n}", effectiveMin)
                          .replace("{x}", effectiveMin - loyalty.balance)}
                      </p>
                    )}
                  </div>
                  );
                })()}
                <button
                  onClick={() => setCheckoutOpen(true)}
                  disabled={r.is_open === false || r.is_open_now === false}
                  className={`btn-primary w-full justify-center mt-5 ${(r.is_open === false || r.is_open_now === false) ? "opacity-40 cursor-not-allowed" : ""}`}
                  data-testid="checkout-btn"
                >
                  {r.is_open === false
                    ? (t.storefront.closedNoOrders || "Ristorante chiuso")
                    : r.is_open_now === false
                    ? (t.storefront.outsideHoursNoOrders || "Fuori orario di apertura")
                    : t.storefront.checkout}
                </button>
              </>
            )}
          </div>
        </aside>
      </div>

      {checkoutOpen && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6 backdrop-blur-sm">
          <form onSubmit={(e) => submitOrder(e, "stripe")} className="bg-deep-card border border-line w-full max-w-lg rounded-t-3xl sm:rounded-3xl p-7 max-h-[92vh] overflow-auto" data-testid="checkout-modal">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-heading text-2xl">{t.storefront.yourInfo}</h2>
              <button type="button" onClick={() => setCheckoutOpen(false)} className="text-zinc-500 text-xl hover:text-zinc-200">×</button>
            </div>

            {!authedCustomer && (
              <div className="card-neon p-5 mb-5 glow-cyan" data-testid="login-required-card">
                <div className="font-heading font-semibold">{t.storefront.loginRequired}</div>
                <p className="text-xs text-zinc-400 mt-1">{t.storefront.loginRequiredSub}</p>
                <button type="button" onClick={startGoogleLogin} className="btn-yellow w-full justify-center mt-4" data-testid="customer-google-login">
                  <svg className="w-4 h-4" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303C33.972 32.91 29.418 36 24 36c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/><path fill="#FF3D00" d="m6.306 14.691 6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/><path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.401 0-9.946-3.067-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/><path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/></svg>
                  {t.storefront.loginGoogle}
                </button>
              </div>
            )}

            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2" data-testid="order-type-radio">
                <button type="button" onClick={() => setOrderType("delivery")}
                  className={`px-3 py-2.5 rounded-xl border text-xs font-mono uppercase tracking-widest transition ${orderType === "delivery" ? "bg-neon-cyan text-[#06061a] border-[#00E5FF] glow-cyan" : "bg-deep border-line text-zinc-300"}`}
                  data-testid="order-type-delivery">
                  {t.storefront.delivery || "Consegna"}
                </button>
                <button type="button" onClick={() => setOrderType("pickup")}
                  className={`px-3 py-2.5 rounded-xl border text-xs font-mono uppercase tracking-widest transition ${orderType === "pickup" ? "bg-neon-yellow text-[#06061a] border-[#FFD600] glow-yellow" : "bg-deep border-line text-zinc-300"}`}
                  data-testid="order-type-pickup">
                  {t.storefront.pickup || "Ritiro"}
                </button>
              </div>
              <Field label={t.storefront.name} value={customer.name} onChange={(v) => setCustomer({ ...customer, name: v })} required testId="co-name" />
              <Field label={t.storefront.phone} value={customer.phone} onChange={(v) => setCustomer({ ...customer, phone: v })} required testId="co-phone" />
              {orderType === "delivery" && (
                <Field label={t.storefront.address} value={customer.address} onChange={(v) => setCustomer({ ...customer, address: v })} required testId="co-address" />
              )}
              {orderType === "pickup" && r.address && (
                <div className="text-xs text-zinc-400 px-3 py-2 rounded-lg border border-dashed border-line bg-deep/40" data-testid="pickup-address">
                  {(t.storefront.pickupAt || "Ritira presso")}: <span className="text-zinc-200">{r.address}</span>
                </div>
              )}
              <div className="space-y-1">
                <label className="input-label flex items-center gap-1.5">
                  📝 {t.storefront.notesLabel || "Note ordine (allergie, preferenze)"}
                </label>
                <textarea
                  value={customer.notes || ""}
                  onChange={(e) => setCustomer({ ...customer, notes: e.target.value })}
                  className="input-neon min-h-[60px] resize-y"
                  placeholder={t.storefront.notesPlaceholder || "Es. no pomodoro, allergico ai latticini, ben cotto..."}
                  data-testid="co-notes"
                  rows={3}
                />
              </div>
            </div>
            <div className="mt-5 flex justify-between text-sm font-heading font-semibold">
              <span>{t.storefront.total}</span>
              <span className="text-neon-cyan text-glow-cyan">€ {total.toFixed(2)}</span>
            </div>
            <div className="mt-5 space-y-2">
              <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-mono">{t.storefront.choosePayment || "Scegli come pagare"}</div>
              {/* Credit card via restaurateur's Stripe Connect (acct_...) */}
              {r.stripe_account_id ? (
                <button type="submit" disabled={submitting || !authedCustomer} className="btn-primary w-full justify-center" data-testid="pay-now-btn">
                  💳 {submitting ? t.storefront.processing : (t.storefront.payNowStripe || "Paga con carta (Stripe)")}
                </button>
              ) : (
                <div className="w-full px-4 py-3 rounded-xl border border-line bg-deep/40 text-center" data-testid="pay-stripe-disabled">
                  <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">💳 {t.storefront.payNowStripe || "Paga con carta"}</div>
                  <div className="text-[10px] text-amber-300 mt-1">{t.storefront.methodInactive || "Metodo non attivo da parte del ristoratore"}</div>
                </div>
              )}
              {/* Satispay via activation code → QR generated at /my-order/{oid} */}
              {(r.satispay_activation_code || r.satispay_handle) ? (
                <button type="button" onClick={(e) => submitOrder(e, "satispay")} disabled={submitting || !authedCustomer} className="btn-outline w-full justify-center !border-[#FF3625] hover:!bg-[#FF3625]/10" data-testid="pay-satispay-btn">
                  <span className="text-[#FF3625] font-bold">Satispay</span>
                </button>
              ) : (
                <div className="w-full px-4 py-3 rounded-xl border border-line bg-deep/40 text-center" data-testid="pay-satispay-disabled">
                  <div className="text-xs font-mono uppercase tracking-widest text-[#FF3625]/70">Satispay</div>
                  <div className="text-[10px] text-amber-300 mt-1">{t.storefront.methodInactive || "Metodo non attivo da parte del ristoratore"}</div>
                </div>
              )}
              {/* Pay-on-Pickup (cash): available ONLY for pickup orders */}
              {orderType === "pickup" && (
                <button type="button" onClick={(e) => submitOrder(e, "cash")} disabled={submitting || !authedCustomer} className="btn-outline w-full justify-center !border-amber-500 hover:!bg-amber-500/10" data-testid="pay-cash-pickup-btn">
                  <span className="text-amber-300 font-bold">💶 {t.storefront.payOnPickup || "Paga al ritiro"}</span>
                </button>
              )}
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, className = "" }) {
  return (
    <div className={`flex justify-between ${className}`}>
      <span className="text-zinc-400">{label}</span>
      <span className="font-medium text-zinc-200">{value}</span>
    </div>
  );
}

function Field({ label, value, onChange, required, testId }) {
  return (
    <div>
      <label className="input-label">{label}{required && " *"}</label>
      <input value={value || ""} onChange={(e) => onChange(e.target.value)} required={required} className="input-neon" data-testid={testId} />
    </div>
  );
}

function ActiveOrderBanner({ order, t }) {
  const isPickup = (order.order_type || "delivery") === "pickup";
  const flow = isPickup
    ? ["paid", "accepted", "preparing", "ready", "delivered"]
    : ["paid", "accepted", "preparing", "out_for_delivery", "delivered"];
  const stepIdx = Math.max(0, flow.indexOf(order.status));
  const progress = Math.min(100, Math.round(((stepIdx + 1) / flow.length) * 100));
  const isCash = order.payment_method === "cash";
  return (
    <div className="card-neon p-5 flex flex-col sm:flex-row items-stretch sm:items-center gap-4 glow-cyan" data-testid="active-order-banner">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-[0.22em] text-neon-cyan font-mono">{t.storefront.activeOrderTag || "Ordine in corso"}</span>
          <span className="chip chip-cyan !text-[9px] !py-0.5 !px-1.5">#{order.id.slice(-6)}</span>
          <span className={`chip !text-[9px] !py-0.5 !px-1.5 ${isPickup ? "chip-yellow" : "chip-cyan"}`}>{isPickup ? (t.storefront.pickup || "RITIRO") : (t.storefront.delivery || "CONSEGNA")}</span>
          {isCash && <span className="chip !text-[9px] !py-0.5 !px-1.5 border-amber-500/40 text-amber-300">{t.storefront.cashPayment || "PAGAMENTO ALL'ARRIVO"}</span>}
        </div>
        <div className="font-heading font-semibold mt-1 text-lg">
          {t.dashboard.mark[order.status] || order.status}
          {order.eta_minutes && order.status !== "delivered" && (
            <span className="ml-3 text-neon-yellow text-sm font-mono">ETA {order.eta_minutes} min</span>
          )}
        </div>
        <div className="mt-3">
          <div className="h-2 rounded-full bg-deep border border-line overflow-hidden" data-testid="active-order-progress">
            <div className="h-full bg-neon-cyan transition-all duration-500" style={{ width: `${progress}%` }} />
          </div>
          <div className="flex justify-between text-[9px] uppercase tracking-widest font-mono text-zinc-500 mt-1">
            {flow.map((st, i) => (
              <span key={st} className={i <= stepIdx ? "text-neon-cyan" : ""}>{(t.dashboard.mark[st] || st).slice(0, 8)}</span>
            ))}
          </div>
        </div>
      </div>
      <Link to={`/my-order/${order.id}`} className="btn-primary text-xs whitespace-nowrap shrink-0 self-start sm:self-center" data-testid="active-order-track-btn">
        {t.storefront.trackOrder || "Traccia ordine"}
      </Link>
    </div>
  );
}
